import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { Warehouse } from '../types/Types';
import { createCodebookCache } from '../utils/codebookCache';

// Active warehouses, shared by every warehouse picker in the app. The factory gives instant paint,
// one request for any number of mounted consumers, and a refresh after any write to warehouses
// (even on screens that stay mounted under open tabs). See utils/codebookCache.ts.
const warehouseCache = createCodebookCache<Warehouse>({
  endpoint: '/api/warehouses/active',
  invalidateOn: '/api/warehouses',
});

/** Active warehouses for dropdowns and lookups (stale-while-revalidate, live-updating). */
export const useWarehouseOptions = (): Warehouse[] => warehouseCache.useOptions();

/** True once the first list has arrived; false only during the very first load. */
export const warehousesLoaded = (): boolean => warehouseCache.peek() !== null;

interface WarehouseCacheContextType {
  getWarehouse: (id: string) => Warehouse | null;
  getWarehouses: () => Warehouse[];
  refreshCache: () => Promise<void>;
  prefetchWarehouses: () => Promise<void>;
  isLoading: boolean;
  // Number of warehouses currently cached — lets a consumer distinguish "not loaded
  // yet" from "genuinely not found".
  size: number;
}

const WarehouseCacheContext = createContext<WarehouseCacheContextType | undefined>(undefined);

/**
 * App-wide id → warehouse lookup, backed by warehouseCache. Kept as a context so the existing
 * consumers (WarehouseSelect, DocumentCreationTable) keep their API; the data itself lives in
 * the shared codebook cache.
 */
export const WarehouseCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const warehouses = useWarehouseOptions();

  const byId = useMemo(() => {
    const map = new Map<string, Warehouse>();
    warehouses.forEach((warehouse) => {
      if (warehouse && warehouse._id) map.set(warehouse._id, warehouse);
    });
    return map;
  }, [warehouses]);

  const getWarehouse = useCallback((id: string): Warehouse | null => byId.get(id) || null, [byId]);
  const getWarehouses = useCallback((): Warehouse[] => Array.from(byId.values()), [byId]);
  const refreshCache = useCallback(() => warehouseCache.refetch(), []);

  const value: WarehouseCacheContextType = {
    getWarehouse,
    getWarehouses,
    refreshCache,
    prefetchWarehouses: refreshCache,
    isLoading: !warehousesLoaded(),
    size: byId.size,
  };

  return <WarehouseCacheContext.Provider value={value}>{children}</WarehouseCacheContext.Provider>;
};

export const useWarehouseCache = (): WarehouseCacheContextType => {
  const context = useContext(WarehouseCacheContext);
  if (!context) {
    throw new Error('useWarehouseCache must be used within WarehouseCacheProvider');
  }
  return context;
};
